export type ChangelogEntry = {
  version: string;
  date: string;
  title: string;
  changes: string[];
};

export const APP_VERSION = "0.56.6";

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: "0.56.6",
    date: "2026-11-05",
    title: "Teachers are emailed when a Monitor & Review is sent to them",
    changes: [
      "Fixed a problem where sending a Monitor & Review to teachers moved it to With teachers but did not notify or email the teachers.",
    ],
  },
  {
    version: "0.56.5",
    date: "2026-11-05",
    title: "Ready to send: Monitor & Reviews go to teachers",
    changes: [
      "On the Ready to send tab, Send Selected now sends a Monitor & Review to the student's teachers for review. Before, it tried to send it to the parent and nothing happened.",
      "If nothing could be sent, the message now says so clearly instead of looking like a confirmation.",
    ],
  },
  {
    version: "0.56.4",
    date: "2026-11-05",
    title: "Mark ready and send to teachers from the Monitor & Review page",
    changes: [
      "A draft Monitor & Review (including ones made by Bulk Generate) now has a Mark ready to send button on the document page, so you can check it and move it on.",
      "A Monitor & Review that is ready now has a Send to teachers button on the document page.",
      "Marking ready or sending to teachers now shows an error if the change did not save, instead of a success message.",
    ],
  },
  {
    version: "0.56.3",
    date: "2026-11-05",
    title: "Keystone flag count shows only to Keystone Leaders",
    changes: [
      "The number of flagged Keystone notes on the Keystone menu now shows only for Keystone Leaders, not system administrators.",
    ],
  },
  {
    version: "0.56.2",
    date: "2026-11-05",
    title: "Bulk Monitor & Reviews include every student",
    changes: [
      "Bulk Generate now creates a Monitor & Review for every student on the Master List, not just those with a sent PLP or Action Plan.",
      "Students without a sent plan get a Monitor & Review filled from their profile. Students who already have one for the period are still skipped.",
    ],
  },
  {
    version: "0.56.1",
    date: "2026-11-05",
    title: "System administrators no longer get document notices",
    changes: [
      "Documents sent for LD review now notify only LD Admins, not system administrators.",
      "Notifications now come from the positions you hold, such as Team Leader or Keystone Leader.",
    ],
  },
  {
    version: "0.56.0",
    date: "2026-11-05",
    title: "All your document lists in one place",
    changes: [
      "Ready to send, Document Tracking and Signed Documents are now tabs on the All Documents page: All, Ready to send, With teachers, With parents and Signed.",
      "Each tab shows how many documents it has, with a line saying what the tab is for.",
      "Everything the old pages did is still there, including sending, reminders, accepting, downloading and filing.",
      "Old links and bookmarks still work and open the matching tab.",
    ],
  },
  {
    version: "0.55.3",
    date: "2026-11-05",
    title: "Clicking a document opens the document",
    changes: [
      "In All Documents and Ready to send, clicking a document's row now opens that document instead of the student's profile.",
    ],
  },
  {
    version: "0.55.2",
    date: "2026-11-05",
    title: "Documents dashboard shows the team's work only",
    changes: [
      "The Your work cards have been removed from the Documents dashboard, which now shows the whole team's documents by status.",
      "A new LD review count shows plans waiting for LD review.",
    ],
  },
  {
    version: "0.55.1",
    date: "2026-11-05",
    title: "A cleaner Documents dashboard",
    changes: [
      "The Documents dashboard is more compact: document counts now sit in one row under By status instead of large tiles.",
      "A new With teachers count shows how many Monitor & Reviews are waiting on teacher reviews.",
      "Your work cards are smaller, and the repeated shortcut cards at the bottom are gone.",
    ],
  },
  {
    version: "0.55.0",
    date: "2026-11-05",
    title: "Keystone eligibility now uses the Pulse rules",
    changes: [
      "The Keystone eligibility list now flags Year 7–10 students with an English or Maths class result under 30%, PAT Reading or Maths at or below the 35th percentile, or a NAPLAN result of Developing or Needs additional support in every area.",
      "Each student shows which rules apply. High support means two or more rules; medium means one.",
      "Keystone enrolments are now shared with Pulse (without any notes), so staff no longer need to enter them twice.",
    ],
  },
  {
    version: "0.54.1",
    date: "2026-11-05",
    title: "Safer document deletes",
    changes: [
      "Deleting a document is now recorded, and documents a teacher has already reviewed can't be deleted by accident.",
    ],
  },
  {
    version: "0.54.0",
    date: "2026-11-05",
    title: "Add any teacher to a Monitor & Review",
    changes: [
      "The LD team can add any teacher to a Monitor & Review, and teachers are added from the student's classes even if they haven't signed in yet. They see it as soon as they sign in.",
    ],
  },
  {
    version: "0.53.0",
    date: "2026-11-05",
    title: "Teachers can sign in without being added",
    changes: [
      "Any teacher listed in SCR Central can now sign in to review Monitor & Review documents.",
    ],
  },
  {
    version: "0.52.5",
    date: "2026-11-02",
    title: "One O'Grady house in filters",
    changes: [
      "The O'Grady house now always shows with its apostrophe, so it appears once in the house filter instead of twice.",
    ],
  },
  {
    version: "0.52.4",
    date: "2026-11-02",
    title: "Behind-the-scenes data access update",
    changes: [
      "Behind-the-scenes data access update; no visible change.",
    ],
  },
  {
    version: "0.52.3",
    date: "2026-10-30",
    title: "Clearer enrolment chips on Keystone eligibility",
    changes: [
      "Enrolled now shows as a green chip and Previously enrolled as a teal chip, so they stand out.",
      "Previously enrolled now also shows for students who are enrolled again.",
    ],
  },
  {
    version: "0.52.2",
    date: "2026-10-30",
    title: "Keystone eligibility shows enrolment history",
    changes: [
      "The Eligibility list now starts with students who aren't in Keystone, so you can see who may need picking up.",
      "Students who were in Keystone before show a Previously enrolled badge, with a filter to hide them.",
    ],
  },
  {
    version: "0.52.1",
    date: "2026-10-30",
    title: "Keystone eligibility list fixed",
    changes: [
      "The Keystone eligibility list now shows every eligible student, with their names.",
      "Percentiles read correctly, for example 2nd and 23rd instead of 2th and 23th.",
    ],
  },
  {
    version: "0.52.0",
    date: "2026-10-30",
    title: "Keystone eligibility now uses the school's PAT and NAPLAN results.",
    changes: [
      "The Keystone eligibility list is now built from the school's PAT and NAPLAN results in SCR Central, so it is no longer empty.",
      "If the list is empty, the page now says why: no report yet, no results loaded, or no students matched.",
    ],
  },
  {
    version: "0.51.0",
    date: "2026-10-30",
    title: "Faster loading",
    changes: [
      "The app now loads each section only when you open it, so the first page appears much faster.",
      "If the app updates while you have it open, it reloads itself once instead of showing an error.",
    ],
  },
  {
    version: "0.50.2",
    date: "2026-10-30",
    title: "Keystone Refresh now works",
    changes: [
      "The Refresh now button on the Keystone eligibility page now works for Keystone leaders.",
      "Behind-the-scenes tidy-up of how the app talks to Synergetic, Schoolbox and SCR Central. No other visible change.",
    ],
  },
  {
    version: "0.50.1",
    date: "2026-10-30",
    title: "Bulk Monitor & Reviews copy from the PLP",
    changes: [
      "Bulk-generated Monitor & Reviews now copy goals and strategies from the student's PLP.",
      "Students with no PLP are filled from their profile, and the review step says so.",
    ],
  },
  {
    version: "0.50.0",
    date: "2026-10-30",
    title: "Security and reliability improvements",
    changes: [
      "Staff accounts that have been switched off can no longer use the app.",
      "When a parent signs or comments on a document, the LD Admin is now notified as well as the document's author.",
      "Behind-the-scenes checks make every part of the app handle requests more safely.",
    ],
  },
  {
    version: "0.49.0",
    date: "2026-10-30",
    title: "Create a Monitor & Review straight from a PLP",
    changes: [
      "A sent PLP now has a Create Monitor & Review button that copies its goals and strategies into a new draft, or opens the draft if one already exists.",
      "Keystone notifications now open the right enrolment's notes instead of the Keystone home page.",
    ],
  },
  {
    version: "0.48.0",
    date: "2026-10-30",
    title: "See which students are missing a plan",
    changes: [
      "Student pages, the Master List and Home now show when a student is marked as needing a PLP or Action Plan but doesn't have a current one.",
      "The Master List has a new Needed but missing filter.",
      "NCCD category and level are now recorded in one place, on the student's profile.",
    ],
  },
  {
    version: "0.47.5",
    date: "2026-10-30",
    title: "Internal improvements",
    changes: [
      "Behind-the-scenes tidy-up of how pages save changes. No change to how the app works.",
    ],
  },
  {
    version: "0.47.4",
    date: "2026-10-30",
    title: "Internal improvements",
    changes: [
      "Tighter security behind the scenes. No change to how the app works.",
    ],
  },
  {
    version: "0.47.3",
    date: "2026-10-30",
    title: "One notification bell",
    changes: [
      "Notifications now live in one place: the bell at the top right of every page, with your unread count and a View all link.",
      "The separate Notifications item in the side menu has been removed.",
    ],
  },
  {
    version: "0.47.2",
    date: "2026-10-30",
    title: "Goals are now entered one per line",
    changes: [
      "Goals are now entered one per line, like strategies, so each can be rated.",
      "Goals already typed on documents that have not been sent are split into one goal per line. Ratings teachers have already given stay with their goals.",
    ],
  },
  {
    version: "0.47.1",
    date: "2026-10-30",
    title: "Goals on Monitor & Review forms are now one list",
    changes: [
      "Goals on Monitor & Review forms are now one Goals list instead of three sections for Beginning, Working Towards and Achieved. Teachers rate each goal Beginning, Working or Achieved.",
      "Existing Monitor & Reviews show their goals combined into the one list, with any repeats removed. Nothing that was written has been lost.",
      "Families now see an overall rating for each goal, followed by each teacher's rating.",
    ],
  },
  {
    version: "0.47.0",
    date: "2026-10-30",
    title: "Teachers rate goals, and families see each teacher's ratings",
    changes: [
      "Teachers now rate each goal on a Monitor & Review as Beginning, Working or Achieved, all in one Rate Goals list.",
      "No rating is required: teachers rate only what applied in their class, and can click a rating again to clear it.",
      "Families now see each teacher's name with the ratings that teacher gave, instead of averages. Other teachers still never see each other's ratings.",
      "Anything a teacher left unrated is hidden, and a teacher who rated nothing is left out of the ratings.",
      "Staff see a new Goal Ratings table with an average for each goal.",
    ],
  },
  {
    version: "0.46.0",
    date: "2026-10-30",
    title: "Record when a parent won't sign, and see when they opened a document",
    changes: [
      "The LD Admin can mark a document as Signature refused when a parent will not sign. It still counts as a complete document for filing and NCCD, and can be undone if the parent signs later.",
      "Parents can still open and read a document marked Signature refused.",
      "Every document sent to parents now shows an NCCD evidence section: when it was sent, who sent it and to whom, each time a parent opened it, and how many days they had to sign.",
      "The evidence can be downloaded as a CSV, and it is included at the end of the downloaded PDF.",
    ],
  },
  {
    version: "0.45.0",
    date: "2026-10-30",
    title: "Simpler document forms and a reflection box for families",
    changes: [
      "No field on a PLP, Action Plan or Monitor & Review is compulsory any more. You can save and submit with fields left blank.",
      "Only PLPs have goals. Action Plans, and Monitor & Reviews of an Action Plan, no longer show a goals section.",
      "A goals section is hidden from teachers and families when no goal has been entered.",
      "Families now write the Student/Family Reflection themselves when they view a Monitor & Review. Staff see it read-only.",
      "Recommended Changes and Next Steps / Outcome have been removed from the Monitor & Review. Anything already written is kept.",
    ],
  },
  {
    version: "0.44.0",
    date: "2026-10-30",
    title: "Easier sign-in for parents",
    changes: [
      "The sign-in page now starts with two clear choices: parent or guardian, or staff member.",
      "Parents get simple step-by-step sign-in with a code emailed to them, and links in document emails take them straight there.",
      "Staff sign in with Microsoft as before; other options are under Other sign-in options.",
    ],
  },
  {
    version: "0.43.1",
    date: "2026-10-30",
    title: "Easier-to-read gold buttons",
    changes: [
      "Text on gold buttons and labels is now darker and easier to read.",
    ],
  },
  {
    version: "0.43.0",
    date: "2026-10-30",
    title: "Faster case notes",
    changes: [
      "Quick Add is easier to use: numbered steps, recent students one tap away, and type and NCCD category as buttons.",
      "After saving, Quick Add stays open and offers to add another note for the same student.",
      "All case note pages have a cleaner, more consistent layout.",
    ],
  },
  {
    version: "0.42.0",
    date: "2026-10-30",
    title: "Keystone pages refreshed",
    changes: [
      "All Keystone pages have a cleaner, more consistent layout.",
      "Leaders can click an LD's caseload chip to show only that LD's enrolments.",
    ],
  },
  {
    version: "0.41.0",
    date: "2026-10-30",
    title: "Tidier settings and admin pages",
    changes: [
      "Settings, Generate documents, the document lists and teacher pages have a cleaner, more consistent layout.",
      "Generate documents is now a clear 3-step flow: choose students, choose a template, then review and generate.",
      "The unused Subject Management page has been removed.",
    ],
  },
  {
    version: "0.40.0",
    date: "2026-10-30",
    title: "Simpler student search",
    changes: [
      "The Students list has a new search bar with year and house filters and a results count.",
      "The unused Subjects column and Assign subjects button have been removed.",
    ],
  },
  {
    version: "0.39.2",
    date: "2026-10-30",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "0.39.1",
    date: "2026-10-30",
    title: "One attendance figure on the student page",
    changes: [
      "Attendance on the student page now shows one consistent figure everywhere: the official school rate from Synergetic.",
      "Partial-day absences now count as part days, and school-approved activities such as excursions no longer count as absences.",
      "Each attendance figure is labelled \"Attendance (year to date)\" with an info icon that explains how it is worked out.",
      "The \"Attendance below 80%\" alert and the figure colours now use the same number you see on the page.",
    ],
  },
  {
    version: "0.39.0",
    date: "2026-10-30",
    title: "A new Home page",
    changes: [
      "When you sign in you now land on a Home page showing what needs your attention, quick actions and your recent activity.",
      "Home is the first item in the menu, on computers and phones.",
    ],
  },
  {
    version: "0.38.0",
    date: "2026-10-30",
    title: "Dashboard cards open the right list",
    changes: [
      "Clicking a card in \"Your work\" now opens a list showing exactly the documents the card counts.",
      "The filter from the card shows as a tag above the list. Click its x to remove it.",
      "Case note search results now show as a sortable table.",
      "Removed the large empty gap above headings on cards, such as Student details and the document form sections.",
    ],
  },
  {
    version: "0.37.0",
    date: "2026-10-30",
    title: "Clearer documents for parents",
    changes: [
      "Parents now see a \"What you need to do\" card at the top of each document.",
      "Parent pages are easier to read and use on a phone, with bigger text and buttons.",
      "Attached files show their type and size, with a clear Download button.",
    ],
  },
  {
    version: "0.36.0",
    date: "2026-10-30",
    title: "A fresh new look",
    changes: [
      "Learning Diversity has a new, cleaner design, shared with the other Sprout OS apps.",
      "One navigation layout for everyone: a side menu on computers and a tab bar along the bottom on phones.",
      "Each page now has a clear heading with breadcrumbs, so you always know where you are.",
      "Status labels on documents and case notes now use one consistent set of colours.",
    ],
  },
  {
    version: "0.35.0",
    date: "2026-10-30",
    title: "Easier document forms with autosave",
    changes: [
      "Document forms now have a section menu with a tick for each finished section.",
      "Drafts save automatically a couple of seconds after you stop typing, and the page shows whether your changes are saved.",
      "Save and Submit for review now stay at the bottom of the screen while you scroll.",
      "A timeline on each document shows where it is: draft, LD review, sent, signed or filed.",
      "The app warns you before you leave a page with unsaved changes.",
    ],
  },
  {
    version: "0.34.0",
    date: "2026-10-30",
    title: "Your work at a glance, and quick student search",
    changes: [
      "The Documents dashboard now opens with cards showing the work waiting for you, based on your role.",
      "Press Ctrl+K (or ⌘K on a Mac) anywhere to search for a student and jump straight to them.",
      "Buttons and text are easier to see and use with a keyboard or screen reader.",
      "Charts on the analytics pages now use consistent colours.",
    ],
  },
  {
    version: "0.33.0",
    date: "2026-10-30",
    title: "Student pages reorganised into tabs",
    changes: [
      "Each student page now opens with a summary card showing year, house, plan types and NCCD level.",
      "Documents, case notes, Keystone and adjustments now sit in their own tabs, and the Back button returns to the tab you were on.",
      "The old student profile link now opens the Adjustments tab.",
    ],
  },
  {
    version: "0.32.0",
    date: "2026-10-30",
    title: "Tidier tables and faster-feeling pages",
    changes: [
      "Document, student and user lists now share one consistent table with sorting and a clear empty message.",
      "Row buttons on Signed Documents, Document Tracking, Flagged Notes and User Management have moved into a \"…\" menu on each row.",
      "Pages show a grey outline of the content while loading instead of a spinner.",
    ],
  },
  {
    version: "0.31.0",
    date: "2026-10-30",
    title: "Clearer messages and confirmations",
    changes: [
      "Pop-up browser alerts are replaced with tidy messages in the corner of the screen.",
      "Before you delete something, the app now asks you to confirm and names exactly what will be deleted.",
      "Error messages are now plain English instead of technical codes.",
    ],
  },
  {
    version: "0.30.4",
    date: "2026-10-30",
    title: "Internal improvements",
    changes: ["No user-facing changes in this release."],
  },
  {
    version: "0.30.3",
    date: "2026-10-29",
    title: "See how often each adjustment is actually used",
    changes: [
      "In Teacher Ratings on a Monitor & Review, each adjustment now shows \"Used in X of Y classes\": how many teachers used it, out of those who scored it or marked it N/A. Teachers who left it blank are not counted.",
      "An adjustment most teachers marked N/A now shows a \"Rarely applicable — review?\" badge, so you can check whether it still belongs in the plan.",
      "Averages are worked out the same way as before. Parents see no change.",
    ],
  },
  {
    version: "0.30.2",
    date: "2026-10-29",
    title: "Parent teacher-score averages are fixed when sent",
    changes: [
      "The average teacher scores a parent sees on a Monitor & Review are now fixed at the moment the document is sent. They only change if the document is sent again.",
      "Parents no longer see how many teachers gave a score.",
    ],
  },
  {
    version: "0.30.1",
    date: "2026-10-29",
    title: "Parents see average teacher scores only",
    changes: [
      "On a Monitor & Review, parents now see only the average teacher score for each adjustment. They no longer see each teacher's name, subjects, scores or comments.",
      "An average is only shown once at least two teachers have scored an adjustment. Until then parents see \"Not enough ratings yet\".",
      "Learning Diversity staff still see every teacher's scores and comments.",
    ],
  },
  {
    version: "0.30.0",
    date: "2026-10-29",
    title: "Average teacher scores on Monitor & Reviews",
    changes: [
      "Learning Diversity staff now see an Average column in the Teacher Ratings table on a Monitor & Review. It shows the average Effectiveness and Frequency score for each strategy, to one decimal place.",
      "Each average says how many teachers gave a score, for example \"2 of 3 teachers rated\". A dash means nobody has scored that strategy yet.",
      "N/A scores are not counted in the average and are shown as a count instead.",
    ],
  },
  {
    version: "0.29.7",
    date: "2026-10-29",
    title: "Teachers only receive their own scores",
    changes: [
      "When a teacher opens a Monitor & Review, the app now sends them only their own scores and comments, never other teachers'.",
      "Nothing changes on screen for teachers or for Learning Diversity staff.",
    ],
  },
  {
    version: "0.29.6",
    date: "2026-10-29",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release. The record kept when a bulk run fills Monitor & Review classes can no longer be written twice for the same document.",
    ],
  },
  {
    version: "0.29.5",
    date: "2026-10-29",
    title: "Teacher scores stay with the right strategy",
    changes: [
      "Each teacher's scores on a Monitor & Review are now saved against the strategy itself, not its place in the list.",
      "If staff reorder, add or remove strategies after teachers have scored, every score stays with the strategy it was given for.",
      "If a strategy's wording is changed after it was scored, it shows as \"Not rated\" rather than carrying a score the teacher gave to different wording.",
    ],
  },
  {
    version: "0.29.4",
    date: "2026-10-29",
    title: "Tighter protection of students' class lists",
    changes: [
      "A student's classes and teachers can now only be looked up by staff who are allowed to see that student's records, and only for students at your school.",
      "Teacher email addresses are no longer sent to the browser when classes are previewed.",
    ],
  },
  {
    version: "0.29.3",
    date: "2026-10-29",
    title: "Lists on the PDF are easy to read",
    changes: [
      "Lists such as Strategies & Interventions now print on the PDF as bullet points, one item per line, instead of as code-like text in brackets and quotes.",
      "Families see the same bullet lists on their copy of the document, and empty lists are left out.",
    ],
  },
  {
    version: "0.29.2",
    date: "2026-10-29",
    title: "Old supporting files are cleared out automatically",
    changes: [
      "Supporting files you remove from a PLP are now permanently deleted 30 days later. The record that a file was attached and removed is kept.",
      "Files that failed the virus check, and uploads that never finished, are deleted automatically.",
      "Files that are still attached to a PLP are never affected.",
    ],
  },
  {
    version: "0.29.1",
    date: "2026-10-29",
    title: "Supporting files are virus-checked",
    changes: [
      "Every supporting file attached to a PLP is now checked for viruses before anyone can open it.",
      "While a file is being checked it shows \"Scanning…\" and can't be opened yet. This usually takes a few seconds, and never more than about 15 minutes.",
      "A file that fails the check shows \"File rejected — failed virus scan\". It is removed and can't be downloaded by staff or families.",
      "Families only ever see files that have passed the check.",
    ],
  },
  {
    version: "0.29.0",
    date: "2026-10-29",
    title: "Modified curriculum on the PLP, with details and supporting files",
    changes: [
      "The PLP now asks \"Is the student on a modified curriculum?\" next to the modified program question.",
      "When you answer Yes to either question, a box appears for the details, and it must be filled in before you save. Answering No hides the box again without losing what was typed.",
      "You can attach supporting files (such as a modified timetable) under each question: PDF, Word, Excel, PNG or JPG, up to 10 MB each. Files can be added or removed until the PLP is sent.",
      "Families see the details and can download the supporting files from their copy of the PLP. The files are also listed on the PDF.",
      "Adding or removing a file after the PLP is signed for the school clears the signature, so it needs to be signed again before it is sent.",
    ],
  },
  {
    version: "0.28.0",
    date: "2026-10-28",
    title: "Open Learning Diversity from the SCR App without signing in again",
    changes: [
      "When you open Learning Diversity from the SCR App, it can now sign you in straight away with your school account, without the Microsoft sign-in page. This is switched on once the SCR App side is ready.",
      "You see exactly what you would after signing in with Microsoft. If you don't have a Learning Diversity account, you are told so, as before.",
      "If the quick sign-in can't be used, you go to the usual Microsoft sign-in instead.",
    ],
  },
  {
    version: "0.27.0",
    date: "2026-10-27",
    title: "Create Monitor & Reviews for a whole group",
    changes: [
      "Generate documents can now create Monitor & Reviews for a group of students at once. Choose Monitor & Reviews instead of PLPs and Action Plans; each run is one or the other.",
      "A student gets a Monitor & Review when they have a PLP or Action Plan for the period that has been sent to the family, and no Monitor & Review for it yet. The preview shows which plan each one is based on, and why any student was skipped.",
      "Each Monitor & Review starts with the student's goals and adjustments from their profile and this semester's classes with their teachers.",
      "Only the LD Admin (or an admin) can create Monitor & Reviews. Other staff are told so instead of seeing a general error.",
      "Only the document types you are allowed to create are offered on the Generate documents page.",
    ],
  },
  {
    version: "0.26.3",
    date: "2026-10-27",
    title: "Generate documents is easier to find",
    changes: [
      "The Documents page now has a Generate documents button beside New document, for creating PLPs and Action Plans for a group of students at once.",
    ],
  },
  {
    version: "0.26.2",
    date: "2026-10-27",
    title: "Monitor & Reviews start from the student's profile",
    changes: [
      "A new Monitor & Review now fills in the student's goals from their Learning Diversity profile, under Beginning, Working Towards or Achieved to match each goal's progress. Goals with no progress recorded yet go under Beginning.",
      "Strategies & Adjustments Being Reviewed now fills in from the adjustments on the student's profile.",
    ],
  },
  {
    version: "0.26.1",
    date: "2026-10-27",
    title: "New PLPs and Action Plans start from the student's profile",
    changes: [
      "When you create a PLP or Action Plan yourself, it now opens pre-filled from the student's Learning Diversity profile — overview, strengths, challenges, goals and adjustments — the same way Bulk Generate does.",
      "It works whichever you choose first, the student or the template. Anything the profile leaves blank is filled from the student's earlier documents.",
      "Nothing you have typed is replaced, and switching to a different student clears the first student's pre-filled details.",
    ],
  },
  {
    version: "0.26.0",
    date: "2026-10-26",
    title: "Teachers get their review accounts automatically",
    changes: [
      "Teachers now get a teacher account in the app automatically, so most Monitor & Review teacher lists fill themselves.",
      "Teachers only see the students and documents they are asked to review, as before.",
      "When a teacher leaves the school, their account is switched off and they stop getting review requests.",
      "A teacher who has never signed in to any school app is listed for the LD Admin to add by hand.",
    ],
  },
  {
    version: "0.25.0",
    date: "2026-10-26",
    title: "Monitor & Reviews fill in classes and teachers for you",
    changes: [
      "A new Monitor & Review now lists the student's current classes and teachers from the school timetable, leaving off BOOST, Learning Enhancement, STRETCH, RUAH, Seminar and Study.",
      "Those teachers are asked to review automatically when it is sent to teachers.",
      "The LD Admin can see which teachers have no teacher account in the app, so nobody is missed.",
      "If no classes are found for a student, the subjects recorded on the student are used, as before.",
    ],
  },
  {
    version: "0.24.7",
    date: "2026-10-26",
    title: "Case notes reach Schoolbox with the right date",
    changes: [
      "Case notes synced to Schoolbox in a batch now carry the date written on the note, instead of no date.",
      "A note sent to Schoolbox without a date now uses today's Melbourne date. Before 11am it was using yesterday's.",
    ],
  },
  {
    version: "0.24.6",
    date: "2026-10-26",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "0.24.5",
    date: "2026-10-26",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "0.24.4",
    date: "2026-10-26",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "0.24.3",
    date: "2026-10-26",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "0.24.2",
    date: "2026-10-22",
    title: "Guardian emails go to personal addresses first",
    changes: [
      "When a guardian has a personal email, documents and reminders now go there. Staff who are also parents no longer get family mail at their school address.",
    ],
  },
  {
    version: "0.24.1",
    date: "2026-10-22",
    title: "Parents only see documents sent to them from the Send window",
    changes: [
      "A family can only open a document if they were chosen as a recipient when it was sent. The two documents sent before guardians could be chosen have been given their recipient, so nothing changes for those families.",
      "Reminder emails go only to the guardians a document was sent to.",
      "LD Assistants can no longer change documents behind the scenes. They already had no document screens, so nothing changes in what they see.",
    ],
  },
  {
    version: "0.24.0",
    date: "2026-10-22",
    title: "See and undo bulk document runs",
    changes: [
      "Generate documents now keeps a list of recent runs: when each ran, the period, who ran it, and how many documents it created, skipped or could not create.",
      "A run made by mistake (for example against the wrong period) can be undone. Undo removes only the documents that are still untouched drafts; anything someone has already edited, sent or signed is kept.",
      "Only the person who ran it, or an admin, can undo a run.",
    ],
  },
  {
    version: "0.23.3",
    date: "2026-10-22",
    title: "Internal improvements",
    changes: ["No user-facing changes in this release."],
  },
  {
    version: "0.23.2",
    date: "2026-10-22",
    title: "Internal improvements",
    changes: ["No user-facing changes in this release."],
  },
  {
    version: "0.23.1",
    date: "2026-10-22",
    title: "Internal improvements",
    changes: ["No user-facing changes in this release."],
  },
  {
    version: "0.23.0",
    date: "2026-10-22",
    title: "Action Plans and PLPs are signed for the school before they go to parents",
    changes: [
      "Before an Action Plan or PLP can be sent to parents, the LD Admin (or an admin) signs it by hand in the Send window.",
      "The school signature, with the signer's name and date, appears on the parent's view of the document and on the downloaded PDF.",
      "If the document is changed or returned to draft after it was signed, the signature is removed and it needs to be signed again.",
      "Monitor & Review documents don't need a school signature.",
    ],
  },
  {
    version: "0.22.1",
    date: "2026-10-22",
    title: "Documents only go to guardians checked in SCR Central",
    changes: [
      "Sending several documents at once from Ready to send now sends each one to the student's primary contact in SCR Central. If a student has no primary contact with an email address, that document is left unsent and the message tells you which one, so you can open it and choose a guardian.",
      "A document's parent contact details can no longer be changed by hand. They are set when you send the document and choose who receives it.",
    ],
  },
  {
    version: "0.22.0",
    date: "2026-10-22",
    title: "Who does each document step, LD review, and filing",
    changes: [
      "Each document step now belongs to a role. Inclusive Practice Leaders create Action Plans and PLPs. The LD Admin reviews them, runs Monitor & Reviews, sends documents to parents, accepts signatures and files them. Admins can still do everything.",
      "Important for admins: give people the LD Admin Assistant or Inclusive Practice Leader position in Settings → Positions. Until you do, only admins can move documents along, and everyone else will see a message saying who can.",
      "Action Plans and PLPs now go to the LD Admin for review before they can be sent. The Inclusive Practice Leader clicks Submit for LD review. The LD Admin then approves the plan, or returns it with comments the IPL can see on the document.",
      "Everyone involved is notified at each handover: when a plan is submitted, approved or returned.",
      "Only the LD Admin can remind, skip, add or remove teachers on a Monitor & Review. Other staff can still see who has reviewed.",
      "A new Filing step: after accepting a signature, the LD Admin ticks each place the signed document has been filed. Each tick records who did it and when. Once every place is ticked, the document shows Filed.",
      "Admins choose the filing places in Settings → Filing Locations.",
    ],
  },
  {
    version: "0.21.0",
    date: "2026-10-22",
    title: "Choose which parents receive a document",
    changes: [
      "When you send a document to a family, you now see every parent and guardian linked to the student in SCR Central, with their relationship and email address, and tick one or more of them.",
      "Each person you choose gets their own email and signs in with their own code. One signature completes the document; the others can still open and read it.",
      "Parents or guardians without an email address are listed but can't be ticked, so you know to contact them another way.",
      "The app records who chose which parents for each document.",
    ],
  },
  {
    version: "0.20.0",
    date: "2026-10-22",
    title: "Parents can sign in with an emailed code",
    changes: [
      "Parents and guardians can now open the documents you send them. They go to the parent sign-in page, enter their email address and type in the 6-digit code we email them. There is no password and no Microsoft account to set up.",
      "The email telling a family a document is ready (and the signature reminder) now links to that sign-in page. It names the school and the kind of document, but not the student.",
      "Codes last 10 minutes and work once. After five wrong tries a new code is needed, and only a few codes can be requested in a short time, to keep accounts safe.",
      "A parent's sign-in lasts two hours, and they can sign out at any time.",
      "Only the email address a document was sent to can open it.",
    ],
  },
  {
    version: "0.19.2",
    date: "2026-10-23",
    title: "New notes show today's date",
    changes: [
      "New case notes and Keystone notes now default to today's date in Melbourne. Before 11am they were showing yesterday's date.",
      "Analytics date ranges, export file names and report dates also use the Melbourne date.",
    ],
  },
  {
    version: "0.19.1",
    date: "2026-10-16",
    title: "Groundwork for Learning Diversity support in the SCR App",
    changes: [
      "Added a read-only link so the SCR App can later show a student's Learning Diversity status and active adjustments to staff allowed to see wellbeing details. Nothing changes in Learning Diversity itself.",
    ],
  },
  {
    version: "0.19.0",
    date: "2026-10-14",
    title: "Manage who reviews a Monitor & Review",
    changes: [
      "A Monitor & Review with teachers now has a reviewer list. Click the people icon on its row in All Documents to see who has reviewed, who is still waiting, and when each was last reminded.",
      "A new Remind teachers button emails only the teachers who haven't reviewed yet. Each teacher is reminded at most once a day per document.",
      "You can skip a teacher who can't review, for example because they are on leave, by giving a reason. The review can then finish without them, and they stop getting reminders.",
      "You can add a teacher who wasn't on the list, or take one off. A teacher who has already reviewed stays on the list.",
      "Once nobody is waiting, the document moves to Reviewed by teachers and can be sent to the parent straight from the same window.",
      "Teachers only see a review in their portal while they are still on the list and waiting.",
    ],
  },
  {
    version: "0.18.3",
    date: "2026-10-14",
    title: "Sensitive profile fields are now private to leaders",
    changes: [
      "Profile fields marked sensitive are now only visible to admins and team, department and inclusive practice leaders. Other staff no longer see those fields on a student's profile.",
      "Settings \u2192 Student profile fields now explains who can see a field when you tick \"This field holds sensitive information\".",
    ],
  },
  {
    version: "0.18.2",
    date: "2026-10-14",
    title: "Groundwork for automatic Monitor & Review subjects",
    changes: [
      "Fixed a permissions problem that would have stopped the app reading the list of classes left off Monitor & Review documents, and the admin corrections to class names and teachers. Nothing uses these yet, so nothing changes on screen.",
    ],
  },
  {
    version: "0.18.1",
    date: "2026-10-14",
    title: "Inclusive Practice Leader position",
    changes: [
      "Admins can now give staff the Inclusive Practice Leader position in Settings \u2192 Positions. It doesn't change what anyone can do yet: it's groundwork for the new Action Plan and PLP sign-off process.",
    ],
  },
  {
    version: "0.18.0",
    date: "2026-10-12",
    title: "Reports, exports, and your own profile fields",
    changes: [
      "A new Reports page shows what kinds of goals are being set across the school, grouped by category, with how many students and how many have been achieved.",
      "It also shows which adjustments are working in which subjects, from the Monitor & Review scores. Each adjustment is listed per subject, because the same adjustment can work well in one classroom and not another. This stays empty until teachers have scored their first Monitor & Review.",
      "Adjustments scored fewer than three times are marked \"thin\" — too few ratings to draw a conclusion from.",
      "\"Not applicable\" ratings are counted separately rather than averaged in, so an adjustment that only applies in some classes is not made to look ineffective.",
      "Modified curriculum goals can be exported as a spreadsheet, with each student's name, ID, year level and house, ready to hand to whoever maintains Synergetic. The app does not write to Synergetic itself.",
      "Admins can now add their own fields to the student profile under Settings, choosing the type and, for choice fields, the options. Fields holding health or wellbeing information can be marked sensitive.",
      "Hiding a custom field removes it from the profile but keeps everything already recorded against it. There is no delete, because deleting a field would take its answers with it.",
    ],
  },
  {
    version: "0.17.0",
    date: "2026-10-12",
    title: "Generate documents for a whole group at once",
    changes: [
      "A new Generate documents page creates documents for a whole cohort in one go, instead of filling in a form per student. Choose the period, the document types and optionally a year level or house, and the app creates whatever each student's profile says they need.",
      "Each document arrives pre-filled from the student's profile — their overview, strengths, challenges, goals and adjustments — so you are editing rather than starting from a blank form.",
      "Nothing is created until you have seen exactly what will be. Press Preview first: it lists every student, what will be created for them, and what they already have.",
      "Running it twice will not create duplicates. Students who already have a document covering that period are skipped and listed as skipped.",
      "If some documents fail, the rest are still created and the failures are named with the reason. Running it again retries only the ones that failed.",
      "Students on Monitoring are not included, because they have no current plan.",
    ],
  },
  {
    version: "0.16.0",
    date: "2026-10-12",
    title: "The student profile",
    changes: [
      "Every student on the Master List now has a Learning Diversity profile page. Click any row to open it.",
      "The top of the page pulls together what the school already knows: photo, year level, house, Oratory, date of birth and tutor group. You do not have to type any of it.",
      "Attendance for the current year is shown with the rate, absences, lates and how many whole days are unexplained. Recent pastoral entries are listed alongside it. Confidential pastoral entries are not shown.",
      "Diagnoses are shown, limited to the condition types your team has chosen as relevant to Learning Diversity. Medications are never displayed.",
      "Goals are now recorded one at a time with a category, and each can be marked Beginning, Working towards or Achieved. Goals can be flagged as modified curriculum goals.",
      "Adjustments and strategies are recorded the same way, as separate items rather than one long paragraph — which is what will let us report on which adjustments are working.",
      "External support programs such as speech pathology or occupational therapy can be recorded, including whether they happen during school hours.",
      "Removing a goal or adjustment retires it rather than deleting it, because plans that have already gone home refer to them.",
      "A student without a profile gets a Create profile button, which is what adds them to the Master List.",
    ],
  },
  {
    version: "0.15.0",
    date: "2026-10-12",
    title: "The Master List",
    changes: [
      "A new Master List shows every student the Learning Diversity team is working with, in one sortable table. Click a column heading to sort by it, and use the chips to filter by status, year level, house, whether a student needs a PLP or an Action Plan, and whether they have any document yet.",
      "Each row shows the student, their year and house, how many goals and adjustments are recorded, and their most recent document. Students with no document at all are called out, so nobody is missed.",
      "This is not the same as the Students page. Students lists everyone the app knows about, including past students; the Master List shows only those with a Learning Diversity profile, so it answers who you are actually working with.",
      "Leavers and students marked Not LD are hidden by default. Turn those chips on to include them.",
      "Sorting, filtering and paging all happen on the server, so the list stays quick as it grows.",
    ],
  },
  {
    version: "0.14.0",
    date: "2026-10-12",
    title: "Internal improvements",
    changes: [
      "Groundwork for the new student profile: somewhere to record a student's goals and adjustments as separate items rather than paragraphs, their external support programs, whether they need a PLP or an Action Plan, and any extra fields your team wants to add. Nothing changes on screen yet — the profile page and the master list come next.",
      "Goals can now be categorised (organisation, interpersonal, literacy and so on), which is what will let us show what kinds of goals are being set across the school.",
    ],
  },
  {
    version: "0.13.7",
    date: "2026-10-12",
    title: "Internal notes stay with Learning Diversity",
    changes: [
      "Learning Diversity working notes on a Monitor & Review (recommended changes and next steps) are now never sent to a family's device, not just hidden on screen.",
      "Parents can only open, comment on and sign their own documents, and a signature must be made at the time it is recorded.",
    ],
  },
  {
    version: "0.13.6",
    date: "2026-10-12",
    title: "Smoother teacher review",
    changes: [
      "The teacher review form now shows its messages in the corner of the screen instead of pop-up boxes that had to be clicked away.",
      "Document Tracking shows the document type in plain words.",
    ],
  },
  {
    version: "0.13.5",
    date: "2026-10-09",
    title: "Students and staff linked to the school directory",
    changes: [
      "Behind the scenes, each student and staff login is now linked to the central school directory so details can be kept in one place later. Nothing changes for staff using the app.",
    ],
  },
  {
    version: "0.13.4",
    date: "2026-10-09",
    title: "Safer database settings",
    changes: [
      "The app now refuses to start if it is not pointed at the Learning Diversity database area, instead of quietly using the wrong one. Nothing changes for staff using the app.",
    ],
  },
  {
    version: "0.13.3",
    date: "2026-10-09",
    title: "Hourly student sync and Keystone background jobs work again",
    changes: [
      "Fixed a fault that stopped the hourly student update from SCR Central, the student refresh, and the Keystone eligibility and enrolment reminder jobs from running. They run again from now on.",
    ],
  },
  {
    version: "0.13.2",
    date: "2026-10-09",
    title: "Tidier document screens",
    changes: [
      "Document lists show each student's student number instead of an internal code.",
      "Signed Documents shows the document's title, and the parent's copy says \"Monitor & Review\" instead of \"monitor-review\".",
      "The teacher review form names each section properly instead of showing a code.",
      "The parent's copy lists sections in the same order as the form.",
    ],
  },
  {
    version: "0.13.1",
    date: "2026-10-09",
    title: "Teachers can see documents waiting for their review",
    changes: [
      "Fixed a problem where the Teacher Portal always showed no pending or completed reviews.",
    ],
  },
  {
    version: "0.13.0",
    date: "2026-10-09",
    title: "See which parents have opened a document, and record the period it covers",
    changes: [
      "Document Tracking now tells you whether a parent has actually opened a document. A document sent three weeks ago and never opened used to look exactly like one a parent read yesterday and is still considering \u2014 both showed as \"Awaiting signature\". There is now an \"Opened\" column, and the status reads \"Not yet opened\" or \"Opened, not signed\".",
      "Two new filters: \"Not opened by the parent\" and \"Opened but not signed\", each showing a count. A family who has not opened the document at all is often better chased by phone than by another email.",
      "Chase priority now accounts for this. An unopened document is escalated after a week; one that has been opened and is presumably being considered is escalated after a fortnight.",
      "Documents now record the period they apply to, with an \"Applies from\" and \"Applies to\" date on the form and a column in tracking. This is separate from when a document was written, sent or signed \u2014 a Monitor & Review written in November covers the semester that is ending, and a plan signed in March may have applied since January.",
      "The opened dates shown are real and go back as far as the app has been recording them; nothing was backfilled or estimated.",
    ],
  },
  {
    version: "0.12.2",
    date: "2026-10-09",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "0.12.1",
    date: "2026-10-09",
    title: "Internal improvements",
    changes: [
      "Groundwork for building Monitor & Review documents from the school's class and teacher records instead of typing them in by hand. Nothing changes in the app yet \u2014 the screens that use this come next.",
    ],
  },
  {
    version: "0.12.0",
    date: "2026-10-09",
    title: "Scoring and the plan forms now match the 2026 paper templates",
    changes: [
      "The frequency scores teachers pick when reviewing a student now mean what the printed Monitor & Review says they mean. A score of 1 was labelled \"Rarely\" in the app but \"never uses adjustment\" on the document, and 3 was \"Often\" against \"consistently uses adjustment\". The same mismatch applied to the 2 on the effectiveness scale, which is \"Occasionally Effective\" on the template. Scores already submitted are unchanged — the numbers were always stored the same way, it was the wording beside them that disagreed.",
      "The teacher review page now shows the full key for both scales, worded exactly as it appears on the paper document, so you are reading the same thing whichever you have in front of you.",
      "Teacher ratings shown on the staff document page and in the parent's copy now use the same wording as the review form and the paper key.",
      "The Action Plan form matches the 2026 template: it now asks for \"Other Things to Note\" instead of \"Teacher Support Strategies\", and no longer asks whether the student is on a modified program. That question belongs to the Personalised Learning Plan and still appears there.",
      "Each template now records who countersigns it (the Year Level Learning Leader for an Action Plan, the Inclusive Practice Leader for a PLP), ready to show on the documents in a later release.",
      "\"Recommended Changes\" and \"Next Steps\" on a Monitor & Review are now marked as Learning Diversity working notes. They are left off the parent's PDF and hidden on the parent's screen.",
    ],
  },
  {
    version: "0.11.25",
    date: "2026-10-09",
    title: "Monitor & Review waits for every teacher",
    changes: [
      "A Monitor & Review document now stays with teachers until all of the student's teachers have reviewed it, instead of closing after the first one.",
      "The All Documents list shows how many teachers have reviewed each document.",
      "If a teacher is away, staff can send the document to the parent with the reviews they have.",
      "Teachers who submit at the same time no longer overwrite each other's reviews.",
    ],
  },
  {
    version: "0.11.24",
    date: "2026-10-09",
    title: "Document notifications reach the right people",
    changes: [
      "Staff are now told when a parent signs or comments on a document, and when teacher reviews come in.",
      "Teachers are now told when a document is waiting for their review, and reminders go to the teachers who haven't reviewed yet.",
      "Parents are told when a document is sent to them, whichever way staff send it.",
      "The Send reminders button on the Tracking page now sends reminders, shows when each document was last reminded, and tells you if someone couldn't be reached.",
      "Notification emails now come from a Learning Diversity address that mail servers accept.",
    ],
  },
  {
    version: "0.11.23",
    date: "2026-10-09",
    title: "Import settings and default commentable fields now save",
    changes: [
      "Saved CSV column mappings for student, document and subject imports now stick, and imports use them.",
      "The admin setting for which document fields parents can comment on now saves, and new documents start with those fields open for comment.",
      "If saving either setting fails, you now see an error instead of a false success message.",
    ],
  },
  {
    version: "0.11.22",
    date: "2026-10-09",
    title: "Document buttons and email links go to the right page",
    changes: [
      "The Create, Edit and Review Feedback buttons on the All Documents page now open the document instead of returning you to the sign-in page.",
      "Links in document notifications and reminder emails now open the right page: parents go to their document, teachers to the review form, and staff to the document.",
    ],
  },
  {
    version: "0.11.21",
    date: "2026-10-09",
    title: "Teacher reviews display correctly",
    changes: [
      "Fixed a problem where opening a document with a teacher review could show an error page instead of the document.",
      "Teacher ratings now show both how well each adjustment worked and how often it was used.",
    ],
  },
  {
    version: "0.11.20",
    date: "2026-10-09",
    title: "Parents can sign documents",
    changes: [
      "Fixed a problem that stopped parents from signing a document.",
      "Parents and teachers can now only change their own part of a document: a parent's comments and signature, or a teacher's review.",
    ],
  },
  {
    version: "0.11.19",
    date: "2026-10-09",
    title: "Creating a new support document works again",
    changes: [
      "Fixed a problem that stopped new PLPs, Action Plans and Monitor & Review documents from saving when the student had no linked parent account.",
    ],
  },
  {
    version: "0.11.16",
    date: "2026-10-08",
    title: "Sync history is now visible to staff only",
    changes: [
      "Only staff can now see the Schoolbox sync history. Nothing changes for staff using the app.",
    ],
  },
  {
    version: "0.11.15",
    date: "2026-10-08",
    title: "Behind-the-scenes database housekeeping",
    changes: [
      "Tidied up how database changes are tracked so problems are caught sooner. Nothing changes for staff using the app.",
      "Removed unused code that tried to save copies of Synergetic medical and attendance details. The student page still shows this information live from Synergetic.",
    ],
  },
  {
    version: "0.11.14",
    date: "2026-10-08",
    title: "Closed a gap that let a signed-in user change their own access level",
    changes: [
      "Fixed a security issue where a signed-in user could change their own access level. Access levels can now only be changed by an administrator through User Management. Nothing changes for staff using the app.",
    ],
  },
  {
    version: "0.11.13",
    date: "2026-10-02",
    title: "Only administrators can archive old case notes",
    changes: [
      "Archiving old case notes moves them out of the main list, so only administrators can now do it. The weekly automatic archive keeps running as before.",
    ],
  },
  {
    version: "0.11.12",
    date: "2026-10-02",
    title: "Tighter protection on staff role checks",
    changes: [
      "The checks that decide who is staff, an admin or a Keystone member can now only be used by people who are signed in. Nothing changes for staff using the app.",
    ],
  },
  {
    version: "0.11.11",
    date: "2026-10-02",
    title: "Files can be attached to case notes again",
    changes: [
      "Attaching a file to a case note had stopped working since mid-August, and existing attachments couldn’t be opened or removed. Files now upload, open and delete again. The notes themselves were never affected.",
    ],
  },
  {
    version: "0.11.10",
    date: "2026-10-02",
    title: "Files can be attached to Keystone notes again",
    changes: [
      "Attaching a file to a Keystone note had stopped working since mid-August. The note saved, but the file never uploaded, so the evidence was lost. Files now attach and save again, both when you write a new note and when you add a file to an existing note. Files attached before mid-August were not affected and are still there.",
    ],
  },
  {
    version: "0.11.9",
    date: "2026-10-01",
    title: "Parent details now come from the school's current contact records",
    changes: [
      "The parent name and email shown against a student, and the Guardians panel on the student page, were being read from an old family list that no longer reflects who the school actually contacts. Most students showed no parent email at all. They now come from the school's current Synergetic contact records, so each student shows their main contact first and almost every student now has a parent email.",
    ],
  },
  {
    version: "0.11.8",
    date: "2026-09-30",
    title: "Keystone analytics now counts every student and note",
    changes: [
      "The Keystone Analytics dashboard and its CSV exports could quietly leave out students and case notes once there were more than about a thousand of each, which made the school reach split and the activity, outcome, attendance and engagement figures look smaller than they really were. They now load every record, so the numbers reflect all of the data.",
    ],
  },
  {
    version: "0.11.7",
    date: "2026-08-28",
    title: "Automated checks now guard the exports, student lists and number boxes",
    changes: [
      "Four recent fixes — the spreadsheet-safe CSV export, the full student list on the document form, the ordering that puts the right parent first, and number boxes ignoring the scroll wheel — were only ever checked by hand. They are now covered by automated checks that run on every change, so those behaviours cannot quietly break again.",
      "Nothing you see or do in the app changes with this release.",
    ],
  },
  {
    version: "0.11.6",
    date: "2026-08-28",
    title: "Scrolling no longer changes a number you have typed",
    changes: [
      "Scrolling the page with the cursor over a minutes or score box used to quietly change the number in it. Those boxes now ignore the scroll wheel, so a value you have entered stays as you typed it. Adjusting a number with the arrow keys still works.",
    ],
  },
  {
    version: "0.11.5",
    date: "2026-08-28",
    title: "Bulk Keystone notes now record the student's year level",
    changes: [
      "When you wrote an observation for a group of students at once, the saved note left out the student's year level, even though writing the same note one student at a time recorded it. Notes written from the bulk page now store the year level like every other note.",
      "Notes already saved from the bulk page are unchanged; only notes written from now on include the year level.",
    ],
  },
  {
    version: "0.11.4",
    date: "2026-08-28",
    title: "Every student now appears in the document student picker",
    changes: [
      "The student list on the document form was loaded in a single request that the server could silently cut short, which meant some students simply never appeared in the picker and there was no error to explain why. The list is now loaded in pages, so all 3,711 students are always present.",
      "The form also now requests only the student fields it actually uses, rather than every column on the student record.",
    ],
  },
  {
    version: "0.11.3",
    date: "2026-08-28",
    title: "Downloaded spreadsheets are safer to open",
    changes: [
      "Exported CSV files can no longer run a formula when opened in Excel or Google Sheets. If a note or name happened to begin with a character a spreadsheet treats as the start of a calculation, it is now shown as plain text instead.",
      "Negative numbers such as score changes are unaffected and still appear as numbers.",
    ],
  },
  {
    version: "0.11.2",
    date: "2026-08-28",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release. Internal fix: the project's own developer tooling and setup guides were still pointing at the old database this app used before it moved to SCR Central, so maintenance work could have been carried out against the wrong copy of your data. All references now point at the live database.",
    ],
  },
  {
    version: "0.11.1",
    date: "2026-08-28",
    title: "Internal improvements",
    changes: [
      "No user-facing changes in this release. Corrected internal documentation that described a permission fix as missing when it is in fact live.",
    ],
  },
  {
    version: "0.11.0",
    date: "2026-08-27",
    title: "Mark a whole Keystone group absent in one go",
    changes: [
      "The Keystone bulk note page now has a \"Mark every selected student absent for this session\" tick-box. Select the students who were away, tick the box, and save once — instead of opening each student's note separately.",
      "Group absences now count towards the attended/absent figures and the attendance rate on the Keystone Analytics page. Absences recorded through the bulk note page before this update were being counted as attended.",
      "If part of a group attended and part was away, save two notes: one for the students who attended, and one with the absent box ticked.",
    ],
  },
  {
    version: "0.10.4",
    date: "2026-08-27",
    title: "Keystone analytics downloads now open properly in Excel",
    changes: [
      "The Keystone analytics download was arriving as one long unreadable line, because the file was being written without any line breaks between rows. Each row is now on its own line and the sections are separated as intended.",
      "Both downloads now open straight into Excel with names and symbols intact, instead of needing the text import wizard.",
      "The two download buttons are now named \"Export summary (CSV)\" (the section-by-section snapshot of the dashboard) and \"Export note rows (CSV)\" (one row per note). They previously had near-identical names and saved over each other with the same file name.",
    ],
  },
  {
    version: "0.10.3",
    date: "2026-08-27",
    title: "Keystone no longer reports a Schoolbox failure that never happened",
    changes: [
      "Enrolling a student in Keystone no longer shows a \"Schoolbox sync failed\" message. The pastoral note was being written to Schoolbox correctly all along — the app was waiting for a confirmation number that Schoolbox does not send back, and treated the missing number as a failure.",
      "The same false alarm was putting a red \"Sync failed\" badge on Keystone notes. New notes will now show as synced.",
      "Enrolments and notes created before this update still carry the old \"Sync failed\" marking. Please do not press \"Retry Schoolbox sync\" on them — the note is already in Schoolbox and retrying would create a second copy.",
      "When a sync genuinely does fail, the message now says why instead of showing the word \"undefined\".",
    ],
  },
  {
    version: "0.10.2",
    date: "2026-08-26",
    title: "Fixed the wrong parent showing as the primary contact",
    changes: [
      "Student records and new documents were often showing an emergency contact instead of the student's actual parent. The parent the school sends reports to is now shown first.",
      "This affected around 6 in 10 students, because Synergetic marks several family members as a primary contact and the app was breaking the tie the wrong way.",
      "Please re-check the parent details on any document created before today — the details saved into an existing document are not changed by this update.",
    ],
  },
  {
    version: "0.10.1",
    date: "2026-08-18",
    title: "Internal improvements",
    changes: [
      "The routine that moves case notes older than three years into the archive is now scheduled to run automatically each week. It had been built but never set to run.",
      "No case notes are old enough to be archived yet, so nothing has moved and nothing changes on screen.",
    ],
  },
  {
    version: "0.10.0",
    date: "2026-08-17",
    title: "Sign in from the school platform launchpad",
    changes: [
      "Learning Diversity can now be opened directly from the school platform launchpad, which signs you straight in without a second login.",
      "The usual Microsoft sign-in on the login page is unchanged.",
    ],
  },
  {
    version: "0.9.16",
    date: "2026-08-12",
    title: "Internal improvements",
    changes: [
      "Behind-the-scenes groundwork to let Learning Diversity run on the school's shared central database. No change to how the app looks or works.",
    ],
  },
  {
    version: "0.9.15",
    date: "2026-07-09",
    title: "Keystone: NCCD category is set once on the enrolment",
    changes: [
      "The NCCD category (QDTP / Supplementary / Substantial / Extensive) is now set once on a student's Keystone enrolment and shown read-only on each observation note — you no longer choose it on every note. A duplicate field introduced in the previous update has been removed, so there's just the one NCCD category.",
    ],
  },
  {
    version: "0.9.14",
    date: "2026-07-09",
    title: "Keystone: level of adjustment is set once on the enrolment",
    changes: [
      "The NCCD level of adjustment is now set only on the Keystone enrolment. On observation notes it's shown for reference (read-only) and carried automatically, so you no longer choose it on every note.",
    ],
  },
  {
    version: "0.9.13",
    date: "2026-07-09",
    title: "Keystone: email alerts for flags and enrolment start/end",
    changes: [
      "When a Keystone note is flagged for the leader, Keystone leaders now get an alert (in-app and by email).",
      "When a Keystone enrolment is created or completed, the assigned LD is notified (in-app and by email) — so being assigned, or an enrolment wrapping up, doesn't get missed.",
    ],
  },
  {
    version: "0.9.12",
    date: "2026-07-09",
    title: "Export the Keystone Analytics dashboard to CSV",
    changes: [
      "The Keystone Analytics page now has an 'Export analytics (CSV)' button that downloads a snapshot of everything on the dashboard — enrolment activity, outcomes, attendance, LD reach, intervention breakdowns, engagement and cohort trends — for sharing or record-keeping.",
    ],
  },
  {
    version: "0.9.5",
    date: "2026-07-09",
    title: "Keystone analytics: attendance, LD reach, and intervention breakdowns",
    changes: [
      "The Keystone Analytics dashboard now shows attendance (how many sessions were attended vs absent, with an attendance rate).",
      "New 'LD student reach' charts show how many students are in the Keystone program vs the rest of the cohort, overall and by year level.",
      "New intervention breakdowns show the mix of intervention types and where interventions are concentrated by year level — highlighting where the need sits.",
    ],
  },
  {
    version: "0.9.1",
    date: "2026-07-09",
    title: "Keystone: engagement trend graphs",
    changes: [
      "A student's Keystone profile now shows an engagement trend graph — each session along the bottom, the 1–4 engagement rating up the side — so you can see at a glance whether engagement is improving over the intervention.",
      "The Keystone Analytics dashboard has a new program-wide engagement overview, showing the average engagement rating across sessions.",
    ],
  },
  {
    version: "0.9.0",
    date: "2026-07-09",
    title: "Keystone form updates: engagement rating, level of adjustment, simpler notes",
    changes: [
      "Observation notes now record an engagement rating from 1 (Not engaged) to 4 (Highly engaged and motivated), replacing the old 'Positive observations' list.",
      "You can set a student's NCCD level of adjustment (QDTP, Supplementary, Substantial, Extensive) on their Keystone enrolment, and it pre-fills onto each observation note so it doesn't need re-entering each session.",
      "On the Initial note, 'How best do they learn?' and 'What helps them learn?' are now a single question — 'Student evaluation – how do they learn best?' — with extra options including 'I do, We do, You do', Visuals and Games.",
      "The Adjustments & strategies list on Keystone observation notes is now focused on Macqlit, Quicksmart, 1:1 support, and Collected from class.",
      "Added a student search box to the Keystone enrolments list.",
    ],
  },
  {
    version: "0.8.9",
    date: "2026-06-18",
    title: "Fix: Student subjects now follow the current semester after a rollover",
    changes: [
      "Student subjects are pulled live from Synergetic, and the app was choosing the semester based on the calendar date — so after the college rolled over to the new semester, students kept showing their previous semester's classes until July. The app now follows the semester Synergetic itself has rolled over to, so the correct subjects appear straight away.",
    ],
  },
  {
    version: "0.8.8",
    date: "2026-06-09",
    title: "Fix: Leaders can now unflag case notes written by other staff",
    changes: [
      "Department and Team Leaders (and LD admin assistants) can now unflag case notes from the Flagged page regardless of who wrote them. Previously, clicking 'Unflag' on another staff member's note failed with a permission error because the database only allowed authors to update their own notes.",
    ],
  },
  {
    version: "0.8.7",
    date: "2026-06-05",
    title: "Fix: Observation (and other) Keystone notes failing to save",
    changes: [
      "Saving a Keystone Observation note no longer fails with 'Failed to record enrolment event'. The note's behind-the-scenes event bookkeeping was being blocked by a database permission gap, which left notes half-saved. Keystone staff can now save Observation, Initial, and Final notes reliably.",
    ],
  },
  {
    version: "0.8.6",
    date: "2026-05-29",
    title: "Fix: Schoolbox sync rejected by unknown tags; Synergetic endpoint crashing",
    changes: [
      "Schoolbox was rejecting every case-note sync with 'Tag Learning Diversity not found' because the tag wasn't pre-created in Schoolbox admin. Tagging is now optional — controlled by a new env var — and disabled by default so sync proceeds cleanly.",
      "Fixed a server-side crash in the Synergetic integration endpoint that was returning 500 on some requests.",
    ],
  },
  {
    version: "0.8.5",
    date: "2026-05-29",
    title: "Internal: restrict visibility of Schoolbox-synced case notes",
    changes: [
      "Case notes pushed to Schoolbox are now explicitly locked down: students and parents cannot see them, and no staff role is automatically granted access. They are visible to Schoolbox administrators only.",
    ],
  },
  {
    version: "0.8.4",
    date: "2026-05-29",
    title: "Schoolbox sync now sends pastoral subtype",
    changes: [
      "Case notes pushed to Schoolbox now include the configured pastoral subtype as well as the type, so they're filed under the right category in Schoolbox's pastoral area.",
    ],
  },
  {
    version: "0.8.3",
    date: "2026-05-29",
    title: "Fix: 'Sync failed' badges no longer appear on case notes",
    changes: [
      "The 'Sync failed' badge that was showing on many case notes is gone. The underlying Schoolbox sync had been pointing at the wrong endpoint, so every attempt failed silently and tagged the note as failed.",
      "The sync has been rewritten to use the correct Schoolbox API. It is currently paused while a Pastoral Type is configured in Schoolbox — case notes will save and display normally in the meantime, with no failure badges.",
      "When sync is re-enabled, new case notes will start pushing to Schoolbox automatically. No action needed from staff.",
    ],
  },
  {
    version: "0.8.2",
    date: "2026-05-29",
    title: "Recent case notes on the student profile are now clickable",
    changes: [
      "Click any note in the Recent case notes section of a student profile to jump straight to it on the full case notes page. The note is scrolled into view and briefly highlighted so it's obvious which one you landed on.",
    ],
  },
  {
    version: "0.8.1",
    date: "2026-05-29",
    title: "Fix: student profile stats and recent notes",
    changes: [
      "The summary cards at the top of a student profile (Notes 30d, Notes 90d, Flagged, Total time) were showing 0 even when the student had recent case notes. They now display the correct counts again.",
      "The Recent Case Notes section on the student profile, which was also blank as a result of the same issue, now lists the student's notes.",
    ],
  },
  {
    version: "0.8.0",
    date: "2026-05-29",
    title: "Leadership can now delete any case note (with audit)",
    changes: [
      "Team Leaders, the Department Leader, LD Admin Assistants, and admins can now delete any case note — useful for removing duplicates, fixing data quality issues, or cleaning up records from staff who have left.",
      "Non-author deletes require typing DELETE to confirm and prompt for an optional reason. Every deletion is recorded — who deleted it, when, the full note content, and the reason — and the log is visible to the original author, leadership, and admins.",
      "Staff continue to delete their own notes the same way as before (one-click confirm).",
    ],
  },
  {
    version: "0.7.0",
    date: "2026-05-29",
    title: "Staff can now edit their case notes",
    changes: [
      "If you spot a mistake in a case note you've added — wrong selection, typo, missed observation — use the new Edit button on the note card to correct it instead of deleting and recreating the note.",
      "Edits are tracked: each note shows an 'Edited' tag after it has been changed, with the timestamp of the last edit visible on hover. The full history of changes is preserved in the audit log so corrections remain accountable.",
      "Note: you can only edit notes you created yourself. Edits do not re-sync to Schoolbox; flags on a note continue to be managed via the Flag button on the card.",
    ],
  },
  {
    version: "0.6.0",
    date: "2026-05-29",
    title: "Search for students when creating a new document",
    changes: [
      "On the new document form, the student dropdown has been replaced with a search box. Start typing a student's name or student ID and matching students appear in a list — no more scrolling through hundreds of names. Keyboard arrows and Enter work too.",
    ],
  },
  {
    version: "0.5.8",
    date: "2026-05-20",
    title: "Fix: primary parent now shown correctly on new document form",
    changes: [
      "When creating a new document, the student's primary parent is now displayed correctly. Previously, an emergency contact could appear as the parent if the primary parent did not have a specific role flag set in SCR Central.",
    ],
  },
  {
    version: "0.5.7",
    date: "2026-05-16",
    title: "Fix: notification dropdown no longer opens off-screen",
    changes: [
      "The bell icon in the sidebar's user section now opens its dropdown to the right of the bell instead of below-left, so the list of notifications stays on-screen. Also tightened the unread-count badge so it no longer looks like a chunky pill for single-digit counts.",
    ],
  },
  {
    version: "0.5.6",
    date: "2026-05-16",
    title: "Security hardening for user administration",
    changes: [
      "Admins can no longer change their own admin status or role, or delete/deactivate their own account, preventing accidental lockout and closing a self-escalation foot-gun.",
    ],
  },
  {
    version: "0.5.5",
    date: "2026-05-16",
    title: "Fix admin pages after security update",
    changes: [
      "Admin pages (Users, Schoolbox lookup, Synergetic lookup, case notes) now correctly send your session token to the server so they load again.",
    ],
  },
  {
    version: "0.5.4",
    date: "2026-05-16",
    title: "Internal stability fix",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "0.5.3",
    date: "2026-05-16",
    title: "Internal security improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "0.5.2",
    date: "2026-05-16",
    title: "Internal security improvements",
    changes: [
      "No user-facing changes in this release.",
    ],
  },
  {
    version: "0.5.1",
    date: "2026-05-15",
    title: "Keystone notes: show student name at the top of each note",
    changes: [
      "Each note now shows the student's name (instead of the author) next to the Initial / Observation / Final badge at the top. The author is still shown in the footer of the card, so nothing is lost — but at a glance you can now see which student a note is about.",
    ],
  },
  {
    version: "0.5.0",
    date: "2026-05-15",
    title: "Keystone: clearer enrolment context, searchable note picker, and author-tagged notes",
    changes: [
      "Renamed the enrolment-level Goal field to Context. This is now where you record why a student was referred to Keystone and who referred them — it's no longer mistaken for the per-cycle goal.",
      "The Initial note's Intervention Goal is now blank by default instead of pre-filling from the enrolment. The goal is meant to be co-written with the student, so it starts fresh each time. The enrolment context is shown above the goal field for reference.",
      "You can now back-date enrolments. When editing an enrolment, change the Enrolment date to whatever you need.",
      "Each note now shows the author's name next to the Initial / Observation / Final badge at the top, so it's obvious at a glance who wrote it.",
      "When writing a note, the enrolment picker is now searchable — start typing a student's name, intervention, or cohort to filter. No more scrolling through a long dropdown.",
      "The Flag action on a note now reads 'Flag Department Leader' to reflect that Keystone is the department and the Department Leader is who reviews flagged notes.",
      "Removed the 'Enrolling a student fires a Schoolbox pastoral note…' note from the enrolment form. It will return once family / student notifications are actually sent.",
    ],
  },
  {
    version: "0.4.0",
    date: "2026-05-08",
    title: 'Bulk delete users from User Management',
    changes: [
      'You can now delete multiple users at once. Tick the checkbox next to each user (or the header checkbox to select every row), then click "Delete selected". A confirmation modal lists exactly who will be removed before anything happens. After confirming, you see a summary of which users were deleted and any that could not be — with the reason — so cleanup over a cohort no longer needs N round trips.',
    ],
  },
  {
    version: "0.3.5",
    date: "2026-05-08",
    title: 'Fix: deleting users from User Management now works',
    changes: [
      'Deleting a user from Settings → User Management no longer shows "Database Error deleting user". Users who have written case notes, Keystone notes, or set eligibility overrides can now be deleted — their records are preserved with the author reference cleared.',
    ],
  },
  {
    version: '0.3.4',
    date: '2026-05-08',
    title: 'Fix: Adding staff who have previously signed in via SSO now works',
    changes: [
      'Staff members who had previously signed in via Microsoft Entra SSO (even just once) could not be added through Settings → Users — the form would show "A user with this email address has already been registered" even though they were never formally added. This is now fixed: adding such a user will correctly complete their registration instead of failing.',
    ],
  },
  {
    version: '0.3.3',
    date: '2026-04-30',
    title: 'Fix: Unflag button now shows an error if the action fails',
    changes: [
      'When unflagging a case note fails, you now see a clear error message instead of the button silently doing nothing. Previously, a permission or network error would be swallowed and the button would appear to do nothing.',
    ],
  },
  {
    version: '0.3.2',
    date: '2026-04-29',
    title: 'Fix: unflagged notes no longer reappear after refresh',
    changes: [
      'Unflagging a case note now correctly saves to the database. Previously, team leaders and department leaders could unflag notes but the change was not persisted — the note would reappear on the next page refresh.',
    ],
  },
  {
    version: '0.3.1',
    date: '2026-04-29',
    title: 'Add in-app version display',
    changes: [
      'You can now see the app version in the bottom-right corner of any page. Click it to see what\'s changed in each release.',
    ],
  },
  {
    version: '0.3.0',
    date: '2026-04-29',
    title: 'Keystone Eligibility Report',
    changes: [
      'Added a new Eligibility page under Keystone (Leader and admin only). It surfaces students in the lowest 20% of their year-level cohort on literacy or numeracy, so the Keystone Leader has a starting list to review each morning.',
      'The list refreshes overnight at 02:00 Melbourne time and pulls standardised assessment data from SCR Central — PAT-R, PAT-Maths, NAPLAN reading/writing/spelling/grammar/numeracy, and internal benchmarks.',
      'Each student is scored using the median of their three most recent sittings per instrument, so a single bad result doesn\'t flag a kid who\'s otherwise tracking well. Students with no recorded data on an instrument are excluded from that ranking entirely.',
      'Already-enrolled students stay on the list with a "Currently enrolled" badge so the Leader can see who\'s being supported and who isn\'t yet.',
      'Leader-curated overrides ("not appropriate for Keystone — reason …") let the Leader suppress students from the list. Overrides default to auto-expire at the end of the academic year.',
      'The Analytics page\'s previously-stubbed Eligibility coverage panel now shows what % of currently-eligible students are enrolled in Keystone.',
      'No PAT or NAPLAN data is currently in SCR Central — once it\'s loaded (Spec 2a, separate work in the SCR Central repo), the eligibility list populates automatically.',
    ],
  },
  {
    version: '0.2.0',
    date: '2026-04-28',
    title: 'New Keystone module',
    changes: [
      'Added a new Keystone module for managing intervention enrolments. The Keystone Leader can enrol students, assign Learning Diversity staff, and end enrolments when the cycle finishes.',
      'Three Keystone case-note types: Initial (intake assessment, learning preferences, pre-test, confidence rating), Observation (mirrors the existing Case Notes form), and Final (post-test, what they achieved, final confidence — saving this also closes the enrolment).',
      'Saving an enrolment or note automatically pushes a pastoral note to Schoolbox; failures surface a manual retry on the row.',
      'New roles: Keystone Leader and Keystone LD. Keystone is hidden from staff who don\'t hold one of these roles (or admin). The Keystone Leader works exclusively in Keystone and doesn\'t see the existing Case Notes or Documents modules; Keystone LDs see both worlds.',
      'A single flag on Keystone notes routes to the Keystone Leader, with a dashboard widget and navbar badge for actioning them.',
      'A new Keystone analytics page covers activity (notes, time spent), outcomes (pre/post score deltas, confidence deltas, completion rates, learning preference distribution) and cohort comparison (year-over-year and by cohort label), plus CSV export.',
      'Settings page for managing the Keystone option lists (intervention types, learning preferences, learning supports).',
      'The student profile page now shows whether a student is enrolled in Keystone. General LD staff see the enrolment status, intervention type and assigned staff; full note content stays visible only to Keystone members.',
    ],
  },
  {
    version: '0.1.1',
    date: '2026-04-24',
    title: 'Fix: students list now shows every student',
    changes: [
      'Searching on the Students page now finds every student, not just the first 1,000 alphabetically. If a student\'s name sorted past the cut-off, searching for them used to return nothing even though their profile was reachable from other screens.',
    ],
  },
  {
    version: '0.1.0',
    date: '2026-04-24',
    title: 'Module-neutral students and Synergetic field locks',
    changes: [
      'Students is now a top-level section available from every module, not only Documents.',
      'Core student details (name, ID, date of birth, house, year level) are locked in the student form — they sync automatically from Synergetic / SCR Central.',
      'The student form now shows a banner explaining which fields sync from Synergetic and which remain editable here (diagnosis, NCCD category, parent contact).',
      'Removed the redundant "Manage Subjects" button from the student detail page.',
      'Introduced versioned release notes so you can see what changed in each update.',
    ],
  },
];
